import { Component, HostListener, output, inject } from "@angular/core";
import {sidebarMenuItems} from "@config/menus/sidebar";
import {TranslateService} from "@app/shared/_services/translate.service";
import {Observable} from "rxjs";
import {UserService} from "@app/shared/_services/user.service";
import {Plan} from "@app/shared/_interfaces/plan";
import {TsMenuItem} from "@app/shared/_interfaces/menu";
import {environment} from "@environments/environment";
import {getNewVersionAlert} from "@app/shared/_factories/newversion.alert";
import {DomainConfigService} from "@app/shared/_services/domain.config.service";
import {map} from "rxjs/operators";
import {turboSMTPApiUrl} from "@config/config";
import {MyAccountService} from "@app/features/myaccount/myaccount.service";
import { MenuModule } from "primeng/menu";
import { CarouselModule } from "primeng/carousel";
import { PrimeTemplate } from "primeng/api";
import { Button } from "primeng/button";
import { RouterLink } from "@angular/router";
import { AsyncPipe, LowerCasePipe, DecimalPipe, DatePipe } from "@angular/common";
import { TranslatePipe } from "../../_pipes/translate.pipe";

@Component({
    selector: "app-sidebar-menu",
    templateUrl: "./app-sidebar-menu.component.html",
    styleUrls: ["./app-sidebar-menu.component.scss"],
    imports: [MenuModule, CarouselModule, PrimeTemplate, Button, RouterLink, AsyncPipe, LowerCasePipe, DecimalPipe, DatePipe, TranslatePipe]
})
export class AppSidebarMenuComponent {
  private _translateService = inject(TranslateService);
  private _userService = inject(UserService);
  private domainConfigService = inject(DomainConfigService);
  private myAccountService = inject(MyAccountService);

  sidebarMenuItems: TsMenuItem[];
  plans: Observable<Plan.Item[]>;

  readonly onOpenSidebar = output<"smtp_settings" | "dns_settings">();
  readonly onOpenDataLocationSidebar = output<boolean>();
  subaccountConfig$ = this.domainConfigService.userSubaccountConfig;
  isSubaccount: boolean;
  isDev: boolean;
  showGdprBadge: boolean = false;

  private gdprCountries: string[] = [
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
    "GB",
    "IS",
    "LI",
    "NO",
    "CH",
  ];

  constructor() {
    this.plans = this._userService.userPlans;

    this.isSubaccount = this._userService.getInfoValue(
      "is_subaccount"
    ) as boolean;
    if (this.isSubaccount) {
      const logo = this._userService.getInfoValue(
        "subaccount_config.logo"
      ) as string;
      if (logo) {
        this.subaccountConfig$ = this.subaccountConfig$.pipe(
          map((config) => ({
            ...config,
            custom: true,
            logo,
          }))
        );
      }
    }

    if (!this.isSubaccount) {
      this.myAccountService.getDetails().subscribe((details) => {
        if (details && details.country) {
          this.showGdprBadge = this.gdprCountries.includes(details.country);
        }
      });
    }

    this.sidebarMenuItems = sidebarMenuItems
      .filter((menuItem) => {
        return !(menuItem?.restriction === "subaccounts" && this.isSubaccount);
      })
      .filter((menuItem) => {
        return !(
          menuItem?.restriction === "already_configured_sender_domains" &&
          (!this._userService.getInfoValue(
            "show_gettingstarted_section"
          ) as boolean)
        );
      })
      .map((menuItem) => {
        return {
          ...menuItem,
          label:
            menuItem.label && this._translateService.translate(menuItem.label),
        };
      });
  }

  @HostListener("document:keydown.control.shift") onKeydownHandler() {
    if (!environment.production) {
      this.isDev = !this.isDev;
    }
  }

  openSMTPSettings() {
    this.onOpenSidebar.emit("smtp_settings");
  }

  openDNSSettings() {
    this.onOpenSidebar.emit("dns_settings");
  }

  openNewVersionModal() {
    getNewVersionAlert(this._translateService)();
  }

  readonly turboSMTPApiUrl = turboSMTPApiUrl;

  openGdprSidebar() {
    this.onOpenDataLocationSidebar.emit(true);
  }
}
